import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Image, Platform } from 'react-native';
import { showConfirmWithButtons } from '@/utils/common/alert';
import Icon from 'react-native-vector-icons/Ionicons';
import { Invitation } from '@/types/residents/home';
import { COLORS, PADDING, CARD_STYLES } from '@/constants/residents/homeConstants';
import { invitationsStrings } from '@/constants/residents/strings';
import { useResponsive } from '@/hooks/common/useResponsive';
import { RESIDENTS_DESKTOP_COLORS } from '@/constants/residents/desktop/color';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { useAppDispatch } from '@/store';
import { resendInvitation } from '@/store/slices/residents/invitationsSlice';
import { WebConfirmModal } from '@/components/common/modals';
import { useToast } from '@/contexts/admin/ToastContext';
import RNButton from '@/components/common/residents/desktop/Button';

interface InvitationListItemProps {
  invitation: Invitation;
  onAccept: (invitation: Invitation) => void;
  onDecline: (invitation: Invitation) => void;
}

export const InvitationListItem: React.FC<InvitationListItemProps> = ({ 
  invitation, 
  onAccept,
  onDecline 
}) => {
  const dispatch = useAppDispatch();
  const { layoutMode } = useResponsive();
  const isDesktop = layoutMode === 'desktop';
  const isSender = invitation.sender;
  const [showConfirm, setShowConfirm] = React.useState(false);
  const { showSuccess, showError } = useToast();

  const formattedDate = React.useMemo(() => {
    try {
      const date = new Date(invitation.createdAt);
      return date.toLocaleString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
    } catch {
      return '';
    }
  }, [invitation.createdAt]);

  const handleAcceptPress = () => {
    onAccept(invitation);
  };

  const handleDeclinePress = () => {
    // iOS-style confirmation alert
    const title = invitation.sender 
      ? invitationsStrings.confirmCancel.title 
      : invitationsStrings.confirmDecline.title;
    const description = invitation.sender 
      ? invitationsStrings.confirmCancel.description 
      : invitationsStrings.confirmDecline.description;

    if (Platform.OS === 'web') {
      setShowConfirm(true);
      return;
    }

    showConfirmWithButtons(
      title,
      description,
      invitation.sender ? 'Cancel Invitation' : 'Decline',
      'Cancel',
      () => onDecline(invitation),
      () => {}
    );
  };

  if (isDesktop) {
    return (
      <View style={stylesDesktop.card}>
        <WebConfirmModal
          visible={Platform.OS === 'web' && showConfirm}
          title={invitation.sender ? invitationsStrings.confirmCancel.title : invitationsStrings.confirmDecline.title}
          message={invitation.sender ? invitationsStrings.confirmCancel.description : invitationsStrings.confirmDecline.description}
          confirmText={invitation.sender ? invitationsStrings.cell.cancelTitle : invitationsStrings.cell.declineTitle}
          cancelText={'Cancel'}
          onConfirm={async () => {
            try {
              setShowConfirm(false);
              await onDecline(invitation);
              showSuccess(invitation.sender ? 'Invitation cancelled' : 'Invitation declined');
            } catch (error) {
              const message = error instanceof Error ? error.message : 'Failed to update invitation';
              showError(message);
            }
          }}
          onCancel={() => setShowConfirm(false)}
        />
        <View style={stylesDesktop.leftCol}>
          <View style={stylesDesktop.avatarContainer}>
            {invitation.user.photoUrl ? (
              <Image 
                source={{ uri: invitation.user.photoUrl }} 
                style={stylesDesktop.avatarImage}
                resizeMode="cover"
              />
            ) : (
              <Icon name="person" size={28} color={RESIDENTS_DESKTOP_COLORS.ICON_INACTIVE} />
            )}
          </View>
          <View style={stylesDesktop.detailsCol}>
            <Text style={stylesDesktop.name}>{invitation.user.email}</Text>
            <Text style={stylesDesktop.subtitle}>
              {isSender ? invitationsStrings.cell.didInvite : invitationsStrings.cell.wasInvited}
            </Text>
            {!!formattedDate && (
              <Text style={stylesDesktop.invitedOn}>Invited on {formattedDate}</Text>
            )}
          </View>
        </View>
        <View style={stylesDesktop.actionsCol}>
          {!isSender && (
            <RNButton
              title={invitationsStrings.cell.ctaTitle}
              onPress={handleAcceptPress}
              accessibilityLabel={invitationsStrings.cell.ctaTitle}
            />
          )}
          {isSender && (
            <RNButton title="Resend" variant="tertiary" icon={<RESIDENTS_SVG_COMPONENTS.REFRESH width={16} height={16} />} onPress={async () => {
              try {
                await dispatch(resendInvitation(invitation.id)).unwrap();
                showSuccess('Invitation resent');
              } catch (error) {
                const message = error instanceof Error ? error.message : 'Failed to resend invitation';
                showError(message);
              }
            }} />
          )}
          <RNButton
            title={isSender ? invitationsStrings.cell.cancelTitle : invitationsStrings.cell.declineTitle}
            variant="link"
            onPress={() => setShowConfirm(true)}
            accessibilityLabel={isSender ? invitationsStrings.cell.cancelTitle : invitationsStrings.cell.declineTitle}
          />
        </View>
      </View>
    );
  }

  return (
    <View style={styles.card}>
      {/* Web confirmation modal for mobile layout */}
      <WebConfirmModal
        visible={Platform.OS === 'web' && showConfirm}
        title={invitation.sender ? invitationsStrings.confirmCancel.title : invitationsStrings.confirmDecline.title}
        message={invitation.sender ? invitationsStrings.confirmCancel.description : invitationsStrings.confirmDecline.description}
        confirmText={invitation.sender ? invitationsStrings.cell.cancelTitle : invitationsStrings.cell.declineTitle}
        cancelText={'Cancel'}
        onConfirm={async () => {
          try {
            setShowConfirm(false);
            await onDecline(invitation);
            showSuccess(invitation.sender ? 'Invitation cancelled' : 'Invitation declined');
          } catch (error) {
            const message = error instanceof Error ? error.message : 'Failed to update invitation';
            showError(message);
          }
        }}
        onCancel={() => setShowConfirm(false)}
      />
      
      {/* Main horizontal stack - matches iOS mainStackView */}
      <View style={styles.mainStack}>
        {/* Avatar Circle - matches iOS iconView (66x66) */}
        <View style={styles.avatarContainer}>
          {invitation.user.photoUrl ? (
            <Image 
              source={{ uri: invitation.user.photoUrl }} 
              style={styles.avatarImage}
              resizeMode="cover"
            />
          ) : (
            <Icon name="person" size={32} color={COLORS.circleIconPrimaryTint} />
          )}
        </View>

        {/* Right content stack - matches iOS rightContentStackView */}
        <View style={styles.rightContentStack}>
          {/* Label stack - matches iOS labelStackView */}
          <View style={styles.labelStack}>
            <Text style={styles.nameLabel}>{invitation.user.email}</Text>
            <Text style={styles.descriptionLabel}>
              {invitation.sender 
                ? invitationsStrings.cell.didInvite 
                : invitationsStrings.cell.wasInvited}
            </Text>
          </View>

          {/* Action buttons stack */}
          <View style={styles.buttonStack}>
            {/* Accept button - only show for received invitations (matching iOS logic) */}
            {!invitation.sender && (
              <TouchableOpacity 
                style={styles.acceptButton}
                onPress={handleAcceptPress}
                accessibilityRole="button"
                accessibilityLabel={invitationsStrings.cell.ctaTitle}
              >
                <Text style={styles.acceptButtonText}>
                  {invitationsStrings.cell.ctaTitle}
                </Text>
              </TouchableOpacity>
            )}
            
            {/* Decline/Cancel button */}
            <TouchableOpacity 
              style={styles.declineButton}
              onPress={handleDeclinePress}
              accessibilityRole="button"
              accessibilityLabel={invitation.sender ? invitationsStrings.cell.cancelTitle : invitationsStrings.cell.declineTitle}
            >
              <Text style={styles.declineButtonText}>
                {invitation.sender ? invitationsStrings.cell.cancelTitle : invitationsStrings.cell.declineTitle}
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    ...CARD_STYLES, // iOS CardTableViewCell base styling
    marginHorizontal: PADDING.padding4,
    marginBottom: PADDING.padding4,
  },
  mainStack: {
    flexDirection: 'row', // iOS: axis = .horizontal
    alignItems: 'flex-start', // iOS: alignment = .top
    padding: PADDING.padding5, // iOS: .Padding.padding5
    flex: 1,
  },
  avatarContainer: {
    width: 66, // iOS: pinWidthTo(66)
    height: 66, // iOS: pinAspectRatioTo(1.0)
    borderRadius: 33,
    backgroundColor: COLORS.circleIconBackground, // iOS: .CircleIcon.background
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: PADDING.padding5, // iOS: spacing = .Padding.padding5
    overflow: 'hidden',
  },
  avatarImage: {
    width: '100%',
    height: '100%',
    borderRadius: 33,
  },
  rightContentStack: {
    flex: 1,
    flexDirection: 'column', // iOS: axis = .vertical
    alignItems: 'flex-start', // iOS: alignment = .leading
    justifyContent: 'space-between',
    minHeight: 66, // Match avatar height for consistent spacing
  },
  labelStack: {
    flexDirection: 'column', // iOS: axis = .vertical
    alignItems: 'flex-start',
    marginBottom: PADDING.padding4, // iOS: spacing = .Padding.padding4
  },
  nameLabel: {
    fontSize: 16, // iOS: .Connections.primary
    fontWeight: '600',
    color: COLORS.textPrimary, // iOS: .Text.primary
    marginBottom: 2,
  },
  descriptionLabel: {
    fontSize: 13, // iOS: .Connections.secondary
    fontWeight: '400',
    color: COLORS.textSubtitle, // iOS: .Text.subtitle
    lineHeight: 18,
    flexWrap: 'wrap',
  },
  buttonStack: {
    flexDirection: 'column', // iOS: axis = .vertical
    alignItems: 'flex-start',
    gap: PADDING.padding1, // Small spacing between buttons
  },
  acceptButton: {
    width: 150, // iOS: widthAnchor.constraint(equalToConstant: 150)
    height: 40, // iOS: heightAnchor.constraint(equalToConstant: 40)
    backgroundColor: COLORS.primary, // iOS: CSButtonPrimary
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
  },
  acceptButtonText: {
    fontSize: 13, // iOS: .Connections.secondary
    fontWeight: '600',
    color: 'white',
  },
  declineButton: {
    width: 150, // iOS: widthAnchor.constraint(equalToConstant: 150)
    height: 40, // iOS: heightAnchor.constraint(equalToConstant: 40)
    justifyContent: 'center',
    alignItems: 'flex-start', // iOS: contentHorizontalAlignment = .left
  },
  declineButtonText: {
    fontSize: 13, // iOS: .Connections.secondary
    fontWeight: '400',
    color: COLORS.primary, // iOS: .Button.primary
  },
});

const stylesDesktop = StyleSheet.create({
  card: {
    backgroundColor: RESIDENTS_DESKTOP_COLORS.PRIMARY_BACKGROUND,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#2f2f2f',
    paddingVertical: 24,
    paddingHorizontal: 24,
    flexDirection: 'row',
    alignItems: 'stretch',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 16,
    marginHorizontal: 16,
  },
  leftCol: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    paddingRight: 16,
  },
  avatarContainer: {
    width: 60,
    height: 60,
    borderRadius: 28,
    backgroundColor: RESIDENTS_DESKTOP_COLORS.CIRCLE_ICON_BACKGROUND,
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    marginRight: 16,
  },
  avatarImage: {
    width: '100%',
    height: '100%',
  },
  detailsCol: {
    flex: 1,
  },
  name: {
    color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY,
    fontSize: 18,
    fontWeight: '800',
    marginBottom: 4,
  },
  subtitle: {
    color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY,
    fontSize: 16,
    marginBottom: 6,
  },
  invitedOn: {
    color: RESIDENTS_DESKTOP_COLORS.TEXT_TERTIARY,
    fontSize: 14,
    marginTop: 4,
  },
  actionsCol: {
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
  },
  acceptBtn: {
    backgroundColor: RESIDENTS_DESKTOP_COLORS.BUTTON_TERTIARY,
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 8,
  },
  acceptBtnText: {
    color: RESIDENTS_DESKTOP_COLORS.SECONDARY_BACKGROUND,
    fontWeight: '700',
  },
  resendBtn: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
    backgroundColor: RESIDENTS_DESKTOP_COLORS.BUTTON_TERTIARY,
  },
  resendContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  resendText: {
    color: RESIDENTS_DESKTOP_COLORS.SECONDARY_BACKGROUND,
    fontWeight: '700',
  },
  removeText: {
    color: RESIDENTS_DESKTOP_COLORS.BUTTON_TERTIARY,
    fontSize: 14,
    fontWeight: '600',
  },
});